import { motion } from "framer-motion";
import { Factory } from "lucide-react";
import { INFRASTRUCTURE } from "../../data/content";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Tile = ({ item, index }) => {
  const isPlaceholder = !item.url;

  return (
    <motion.figure
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      variants={fadeUp}
      custom={index}

      className={`group relative overflow-hidden border border-ink/10 ${item.span} ${item.ratio}`}
    >
      {isPlaceholder ? (
        // Placeholder mode — typographic, on-brand
        <>
          <div className="absolute inset-0 grid-lines-dark opacity-50" aria-hidden />
          <div className="absolute inset-0 bg-gradient-to-br from-ink via-ink to-[#0d1a3e]" />
          {[
            "top-3 left-3",
            "top-3 right-3",
            "bottom-3 left-3",
            "bottom-3 right-3",
          ].map((pos, k) => (
            <div
              key={k}
              className={`absolute ${pos} w-3 h-3 border-amber-accent/60`}
              style={{
                borderTopWidth: pos.includes("top") ? 1 : 0,
                borderBottomWidth: pos.includes("bottom") ? 1 : 0,
                borderLeftWidth: pos.includes("left") ? 1 : 0,
                borderRightWidth: pos.includes("right") ? 1 : 0,
              }}
              aria-hidden
            />
          ))}
          <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8 text-bone">
            <div className="font-display font-medium text-2xl md:text-3xl leading-[1.05] tracking-tight">
              {item.title}
            </div>
            <div className="mt-2 text-[12px] md:text-[13px] text-bone/60 leading-snug max-w-[34ch] space-y-1">
              {item.caption.split("\n").map((line, k) => (
                <div key={k}>{line}</div>
              ))}
            </div>
          </div>
        </>
      ) : (
        // Real photo mode — all overlays positioned relative to the figure
        <>
          <img
            src={item.url}
            alt={item.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover grayscale-[0.08] saturate-[0.92] group-hover:scale-[1.04] group-hover:grayscale-0 group-hover:saturate-100 transition-all duration-[1200ms] ease-out"
          />
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-ink/80 via-ink/30 to-transparent" />
          
          <figcaption className="absolute inset-x-0 bottom-0 p-5 md:p-6 text-bone">
            <div className="font-display font-medium text-lg md:text-xl leading-tight">
              {item.title}
            </div>
            <div className="mt-1 text-[12px] md:text-[13px] text-bone/75 leading-snug space-y-0.5">
              {item.caption.split("\n").map((line, k) => (
                <div key={k}>{line}</div>
              ))}
            </div>
          </figcaption>
        </>
      )}
    </motion.figure>
  );
};

export const Infrastructure = () => {
  return (
    <section
      id="infrastructure"

      className="scroll-mt-nav py-24 md:py-36 border-t border-ink/10 bg-bone"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-5">
            <div className="flex items-center gap-2 mb-4">
              <Factory className="w-4 h-4 text-amber-accent" strokeWidth={1.5} />
              <span className="overline text-forest-accent">/ 04 — Facility</span>
            </div>
            <h2
              className="font-display font-bold leading-[1.02] tracking-tight text-ink text-balance"
              style={{ fontSize: "clamp(2rem, 4.8vw, 3.75rem)" }}
            >
              Built to make
              <br />
              <span className="italic font-light text-amber-accent">
                precision
              </span>{" "}
              repeatable.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 md:pt-3">
            <p className="text-ink-soft text-base md:text-lg leading-relaxed max-w-xl">
              From inert-blanketed reactors, precision crystallizers, ANFDs and distillation suites to a
              continuously-monitored control room and an in-house QC laboratory
              — our Dahej facility is engineered to make every batch behave
              like the last.
            </p>
          </div>
        </div>

        {/* Editorial gallery grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6 auto-rows-min">
          {INFRASTRUCTURE.map((item, i) => (
            <Tile key={item.id} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
